import { useState } from "react";
import { Popover } from "radix-ui";
import { ArrowUpDownIcon, Check } from "lucide-react";

import { cn } from "@/libs/cn";
import { SortOrderOptions } from "@/modules/common/types/SortOrder";
import { CaretDownIcon, CaretUpIcon } from "@/modules/common/assets/icons";

export interface SortDropdownOption<TField extends string> {
  field: TField;
  label: string;
}

export interface SortDropdownValue<TField extends string> {
  field: TField;
  order: SortOrderOptions;
}

interface SortDropdownProps<TField extends string> {
  options: SortDropdownOption<TField>[];
  /** Estado controlado — `null` significa "sem ordenação" */
  value: SortDropdownValue<TField> | null;
  onChange(value: SortDropdownValue<TField> | null): void;
  /** Texto do cabeçalho do popover (ex.: "Ordenar por") */
  label?: string;
  className?: string;
  align?: "start" | "center" | "end";
}

function SortDropdown<TField extends string>({
  options,
  value,
  onChange,
  label = "Ordenar por",
  className,
  align = "end",
}: SortDropdownProps<TField>) {
  const [open, setOpen] = useState(false);

  const handleSelect = (field: TField) => {
    if (value?.field === field) {
      onChange(null);
      return;
    }

    onChange({ field, order: "asc" });
  };

  const handleToggleDirection = () => {
    if (!value) return;
    onChange({ ...value, order: value.order === "asc" ? "desc" : "asc" });
  };

  const isAscending = value ? value.order === "asc" : true;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={label}
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-white cursor-pointer transition-colors",
          "hover:bg-ui-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-ui-primary/50 disabled:cursor-not-allowed",
          value ? "border-2 border-ui-primary text-ui-primary" : "border-ui-border-muted text-ui-text-muted",
          className,
        )}
      >
        <ArrowUpDownIcon className="size-5" />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align={align}
          sideOffset={4}
          role="listbox"
          className={cn("z-50 w-55 rounded-xl border border-ui-border bg-white shadow-lg", "animate-in fade-in-0 zoom-in-95")}
        >
          <div className="flex items-center justify-between border-b border-ui-border px-3 py-2.5">
            <span className="text-sm font-medium text-ui-text">{label}</span>

            <button
              type="button"
              onClick={handleToggleDirection}
              disabled={!value}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-ui-primary hover:bg-ui-hover transition-colors shrink-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            >
              {isAscending ? (
                <>
                  <CaretUpIcon className="size-3.5" />
                  <span>A → Z</span>
                </>
              ) : (
                <>
                  <CaretDownIcon className="size-3.5" />
                  <span>Z → A</span>
                </>
              )}
            </button>
          </div>

          <div className="flex max-h-70 flex-col gap-0.5 overflow-y-auto p-2">
            {options.map((option) => {
              const isSelected = value?.field === option.field;

              return (
                <div
                  key={option.field}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option.field)}
                  className={cn(
                    "flex cursor-pointer items-center justify-between rounded-lg px-3 py-2.5 transition-colors duration-100 hover:bg-ui-hover",
                    isSelected && "bg-ui-hover",
                  )}
                >
                  <span className="truncate text-sm font-medium text-ui-text select-none" title={option.label}>
                    {option.label}
                  </span>

                  {isSelected && <Check className="w-4 h-4" />}
                </div>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default SortDropdown;
